/**
 * API клиент для notifications-service
 */
import { apiClient } from '@/api'
import type {
  ChannelPreference,
  NotificationsListResponse,
  TelegramBotLinkCode,
  TelegramBotStatus,
  TypePreference,
  WebPushSubscriptionData,
} from '../types/notifications.types'

const BASE_PATH = '/api/v1/notifications'

export const notificationsApi = {
  // --- Настройки каналов ---

  async getPreferences(): Promise<ChannelPreference[]> {
    const { data } = await apiClient.get(`${BASE_PATH}/preferences`)
    return data.preferences ?? []
  },

  async updatePreferences(preferences: ChannelPreference[]): Promise<void> {
    await apiClient.put(`${BASE_PATH}/preferences`, { preferences })
  },

  // --- Настройки по типам событий (DEV-701/DEV-767): нет записи = включён ---

  async getEventPreferences(): Promise<TypePreference[]> {
    const { data } = await apiClient.get(`${BASE_PATH}/preferences/events`)
    return data.preferences ?? []
  },

  async updateEventPreferences(preferences: TypePreference[]): Promise<void> {
    await apiClient.put(`${BASE_PATH}/preferences/events`, { preferences })
  },

  // --- Web Push ---

  async getVAPIDPublicKey(): Promise<string> {
    const { data } = await apiClient.get(`${BASE_PATH}/webpush/vapid-key`)
    return data.vapidPublicKey
  },

  async registerWebPush(subscription: WebPushSubscriptionData): Promise<string> {
    const { data } = await apiClient.post(`${BASE_PATH}/webpush/subscribe`, {
      endpoint: subscription.endpoint,
      p256dh_key: subscription.p256dhKey,
      auth_key: subscription.authKey,
      user_agent: subscription.userAgent,
    })
    return data.subscriptionId
  },

  async unregisterWebPush(endpoint: string): Promise<void> {
    await apiClient.post(`${BASE_PATH}/webpush/unsubscribe`, { endpoint })
  },

  // --- Telegram Bot ---

  async getTelegramBotLinkCode(): Promise<TelegramBotLinkCode> {
    const { data } = await apiClient.get(`${BASE_PATH}/telegram/link-code`)
    return {
      linkCode: data.linkCode,
      botUsername: data.botUsername,
      deepLink: data.deepLink,
      expiresInSeconds: data.expiresInSeconds,
    }
  },

  async unlinkTelegramBot(): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/telegram`)
  },

  async getTelegramBotStatus(): Promise<TelegramBotStatus> {
    const { data } = await apiClient.get(`${BASE_PATH}/telegram/status`)
    return {
      linked: data.linked,
      telegramUsername: data.telegramUsername,
      verified: data.verified,
    }
  },

  // --- История уведомлений ---

  async listNotifications(page = 1, pageSize = 20): Promise<NotificationsListResponse> {
    // DEV-302: пагинация — вложенный объект vinprop.common.v1.PageRequest/PageInfo (uint64 → строки)
    const { data } = await apiClient.get(BASE_PATH, {
      params: { 'pagination.page': page, 'pagination.perPage': pageSize },
    })
    return {
      notifications: (data.notifications ?? []).map(mapNotification),
      total: parseInt(data.pagination?.total ?? '0', 10) || 0,
      page: parseInt(data.pagination?.page ?? '0', 10) || page,
      pageSize: parseInt(data.pagination?.perPage ?? '0', 10) || pageSize,
    }
  },

  async markRead(notificationId: string): Promise<void> {
    await apiClient.post(`${BASE_PATH}/${notificationId}/read`)
  },

  async markAllRead(): Promise<number> {
    const { data } = await apiClient.post(`${BASE_PATH}/read-all`)
    return data.markedCount ?? 0
  },

  async getUnreadCount(): Promise<number> {
    const { data } = await apiClient.get(`${BASE_PATH}/unread-count`)
    return data.count ?? 0
  },
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapNotification(raw: any) {
  let metadata: Record<string, unknown> = {}
  if (raw.metadata) {
    try {
      metadata = typeof raw.metadata === 'string' ? JSON.parse(raw.metadata) : raw.metadata
    } catch {
      metadata = {}
    }
  }

  return {
    id: raw.id,
    notificationType: raw.notificationType ?? raw.notification_type,
    title: raw.title,
    body: raw.body,
    metadata,
    channelsSent: raw.channelsSent ?? raw.channels_sent ?? [],
    isRead: raw.isRead ?? raw.is_read ?? false,
    createdAt: raw.createdAt ?? raw.created_at,
    readAt: raw.readAt ?? raw.read_at ?? null,
  }
}
